import * as React from 'react';
import styled, { css } from 'styled-components';

import { AquaTheme as T } from './AquaTheme';

// A port of AquaUI's GlassPane (containers/GlassPane). Keep the material in
// step with AquaUI; see its documentation for why each value is what it is.

interface IGlassPaneProps {
  /** @ignore */
  className?: string;
  /** @ignore */
  children?: React.ReactNode;
  /**
   * If set, adds a shiny border around the component.
   * @default false
   */
  bordered?: boolean;
  /**
   * If set, adds 15px of inner padding to the pane.
   * @default false
   */
  padded?: boolean;
  /**
   * If true, fades in and blurs smoothly on mount via a CSS transition.
   * @default false
   */
  animated?: boolean;
  /** Optional inline styles applied to the outer element. */
  style?: React.CSSProperties;
}

const GlassPaneBase = React.memo((props: IGlassPaneProps) => {
  // Animated panels are mounted after 1 tick so that a CSS transition will run.
  const [mounted, setMounted] = React.useState(props.animated ? false : true);

  React.useEffect(() => {
    if (props.animated) {
      const timeout = window.requestAnimationFrame(() => setMounted(true));
      return () => window.cancelAnimationFrame(timeout);
    } else {
      return () => {};
    }
  }, [props.animated]);

  return (
    <div className={`${props.className} ${mounted ? 'mounted' : ''}`} style={props.style}>
      {props.children}
    </div>
  )
});

/** Enough to destroy detail behind the pane. Below ~16 the backdrop stays legible. */
const BLUR = 24;
/** Puts back the colour the blur averages away. */
const SATURATE = 180;
/** A small lift so the tint does not go muddy over a dark backdrop. */
const BRIGHTNESS = 1.08;
/** The legibility dial: 0.62 is around 4.5:1 for small dark text over a dark map. */
const TINT_ALPHA = 0.62;

const material = css`
  backdrop-filter: blur(${BLUR}px) saturate(${SATURATE}%) brightness(${BRIGHTNESS});
  -webkit-backdrop-filter: blur(${BLUR}px) saturate(${SATURATE}%) brightness(${BRIGHTNESS});
`;

const GlassPaneStyled = styled(GlassPaneBase)`
  position: relative;
  z-index: 1;
  ${p => p.padded && css`padding: 15px;`};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;

  color: ${T.colors.primary[4]};
  border-radius: ${T.radius.normal}px;

  // The tint, with a soft pool of light at the top-left and a cool shadow
  // gathering at the bottom.
  background-color: rgba(255, 255, 255, ${TINT_ALPHA});
  background-image:
    radial-gradient(
      125% 95% at 0% 0%,
      rgba(255, 255, 255, 0.5) 0%,
      rgba(255, 255, 255, 0.14) 42%,
      rgba(255, 255, 255, 0) 72%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.28) 0%,
      rgba(255, 255, 255, 0) 30%,
      rgba(96, 122, 140, 0.05) 78%,
      rgba(96, 122, 140, 0.12) 100%
    );

  ${material}

  // A wide soft shadow for height, and a tight dark one for the contact edge.
  box-shadow:
    0 10px 28px -8px rgba(12, 22, 29, 0.32),
    0 2px 6px -2px rgba(12, 22, 29, 0.24);

  ${p => p.animated && css`
    opacity: 0;
    backdrop-filter: blur(0) saturate(100%) brightness(1);
    -webkit-backdrop-filter: blur(0) saturate(100%) brightness(1);
    transition: opacity 420ms ease, backdrop-filter 420ms ease, -webkit-backdrop-filter 420ms ease;

    &.mounted {
      opacity: 1;
      ${material}
    }
  `}

  // The specular rim: a 1px gradient border, drawn by masking a pseudo-element
  // down to its own padding ring. Lit from above, rising again at the bottom.
  ${p => p.bordered && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(
        165deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(255, 255, 255, 0.45) 26%,
        rgba(255, 255, 255, 0.16) 55%,
        rgba(255, 255, 255, 0.38) 100%
      );
      -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      mask-composite: exclude;
      pointer-events: none;
    }
  `}

  // Without backdrop-filter there is no glass, only a tint, so fall back to a
  // near-solid card.
  @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    background-color: rgba(255, 255, 255, 0.93);
  }
`

/**
 * A `GlassPane` is a frosted panel for use over a colorful background, like
 * a map. Content inherits dark ink.
 */
const GlassPane = ({
  bordered = false,
  padded = false,
  animated = false,
  ...props }: IGlassPaneProps) =>
  <GlassPaneStyled
    bordered={bordered}
    padded={padded}
    animated={animated}
    {...props}
  />;

/** Small uppercase heading used at the top of map panels. */
const GlassPaneCaption = styled.div`
  font: ${T.font.labelCaps};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${T.colors.primary[2]};
`

export { GlassPane, GlassPaneCaption, IGlassPaneProps }
